import type { InstrumentId } from "../scheduling/ScheduledEvent";
import type { DisposeRegistry } from "./DisposeRegistry";
import type { ClickVoiceLike, DrumVoiceKind, ToneAdapter } from "./tone";

/**
 * The Phase 1A voice pool.
 *
 * Created once at init and never reallocated — a voice per note would make the
 * node count climb, which the diagnostics explicitly watch for.
 */
const DRUM_KINDS: DrumVoiceKind[] = ["kick", "pulse", "percussion", "accent"];

export class DrumKit {
  private readonly voices = new Map<InstrumentId, ClickVoiceLike>();
  private active = 0;

  constructor(tone: ToneAdapter, disposeRegistry: DisposeRegistry) {
    for (const kind of DRUM_KINDS) {
      this.voices.set(kind, disposeRegistry.register(tone.createDrumVoice(kind)));
    }
  }

  has(instrument: InstrumentId): boolean {
    return this.voices.has(instrument);
  }

  trigger(
    instrument: InstrumentId,
    midi: number | null,
    durationSec: number,
    timeSec: number,
    velocity: number,
  ): void {
    const voice = this.voices.get(instrument);
    if (voice === undefined) return;
    voice.trigger(midi, durationSec, timeSec, velocity);
    // These are one-shot percussion voices, so "active" is a rough indicator
    // for diagnostics rather than an exact polyphony count.
    this.active = Math.min(this.voices.size, this.active + 1);
  }

  releaseAll(): void {
    for (const voice of this.voices.values()) voice.releaseAll();
    this.active = 0;
  }

  get activeVoiceCount(): number {
    return this.active;
  }

  /** Decays the rough active count so it does not sit pinned at its maximum. */
  decay(): void {
    if (this.active > 0) this.active -= 1;
  }
}
